import { MessageSquare } from "lucide-react";
import { formatPaymentLogDateTime } from "../../utils/applicationFormatters";
import { EditIconButton } from "@/components/ui/EditIconButton";
import { MentionTextarea } from "@/components/notes/MentionTextarea";
import {
  canEditNote,
  classifyNote,
  getNoteRowId,
} from "../../utils/notesTabHelpers";
import styles from "../../styles/applicationDetail.module.css";
import editStyles from "../../styles/editForm.module.css";
import type { NotesAttachment } from "../../types/tabs";
import type { NotesTabNotesSectionProps } from "../../types";
import { ApplicationNotesListEmpty } from "./ApplicationNotesListEmpty";

export function NotesTabNotesSection({ tab }: NotesTabNotesSectionProps) {
  const {
    canViewApplicationNotes,
    canEditApplicationNotes,
    isLibertyRole,
    currentUserId,
    notes,
    filteredNotes,
    noteFilter,
    setNoteFilter,
    newNote,
    setNewNote,
    submittingNote,
    handleAddNote,
    handleTaggingAdd,
    setEditingNote,
    handlePreviewNoteAttachment,
    handleRemoveNoteAttachment,
    removingNoteAttachmentId,
  } = tab;

  if (!canViewApplicationNotes) return null;

  return (
    <div
      className={styles.notesUnifiedSection}
      data-testid="application-notes-tab-section-notes"
    >
      {canEditApplicationNotes ? (
        <div
          className={styles.notesUnifiedBlock}
          data-testid="application-notes-tab-section-add-note"
        >
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionTitleIcon} aria-hidden="true">
              <MessageSquare className="h-4 w-4" />
            </span>
            Add Notes
          </h2>
          <div className={editStyles.formRow}>
            <div className={editStyles.formGroupFull}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <label
                  className={editStyles.label}
                  style={{ marginBottom: 0 }}
                  htmlFor="application-add-note"
                  data-testid="application-notes-tab-add-note-label"
                >
                  Note
                </label>
                <button
                  type="button"
                  className={styles.applicationAddNoteSubmit}
                  style={{ padding: "6px 16px", alignSelf: "auto" }}
                  data-testid="application-notes-tab-add-note-submit-button"
                  onClick={() => void handleAddNote()}
                  disabled={submittingNote || !newNote.trim()}
                >
                  {submittingNote ? "Submitting..." : "Submit"}
                </button>
              </div>
              <div className={styles.addNoteComposer} style={{ gap: 0 }}>
                <MentionTextarea
                  textareaId="application-add-note"
                  wrapperClassName={styles.addNoteMentionWrap}
                  className={editStyles.textarea}
                  placeholder="Add a note… (use @ to mention Liberty users)"
                  value={newNote}
                  onChange={setNewNote}
                  onTaggingAdd={handleTaggingAdd}
                  disabled={submittingNote}
                  rows={5}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    resize: "vertical",
                    minHeight: "120px",
                  }}
                  dataTestId="application-notes-tab-add-note-input"
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className={styles.section} data-testid="application-notes-tab-section-recent-notes">
        {notes.length === 0 ? (
          <ApplicationNotesListEmpty testId="application-notes-tab-empty-notes" />
        ) : (
          <>
            <h6 className={styles.recentNoteHeading}>Recent Notes</h6>

            <div className={`${styles.noteFilterRow} ${styles.recentNoteFilterRow}`}>
              <label
                className={editStyles.radioLabel}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  margin: 0,
                }}
              >
                <input
                  type="radio"
                  name="noteFilter"
                  data-testid="application-notes-tab-filter-all-input"
                  value="all"
                  checked={noteFilter === "all"}
                  onChange={() => setNoteFilter("all")}
                />
                <span>All</span>
              </label>
              <label
                className={editStyles.radioLabel}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  margin: 0,
                }}
              >
                <input
                  type="radio"
                  name="noteFilter"
                  data-testid="application-notes-tab-filter-system-input"
                  value="system"
                  checked={noteFilter === "system"}
                  onChange={() => setNoteFilter("system")}
                />
                <span>System Notes</span>
              </label>
              <label
                className={editStyles.radioLabel}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  margin: 0,
                }}
              >
                <input
                  type="radio"
                  name="noteFilter"
                  data-testid="application-notes-tab-filter-user-input"
                  value="user"
                  checked={noteFilter === "user"}
                  onChange={() => setNoteFilter("user")}
                />
                <span>User Notes</span>
              </label>
            </div>

            <div className={`${styles.notesListPanel} ${styles.recentNoteListPanel}`}>
              {filteredNotes.length > 0 ? (
                filteredNotes.map((note, index: number) => {
                  const kind = classifyNote(note);
                  const isSystem = kind === "system";
                  const noteRowId = getNoteRowId(note);
                  const showEditButton =
                    canEditNote(note, { isLibertyRole, currentUserId }) &&
                    noteRowId != null;
                  return (
                    <div
                      key={index}
                      data-testid={`application-notes-tab-note-row-${noteRowId ?? index}`}
                      className={`${styles.recentNoteRow} ${
                        index % 2 === 0
                          ? styles.recentNoteRowEven
                          : styles.recentNoteRowOdd
                      }`}
                    >
                      <div className={styles.recentNoteMeta}>
                        <strong className={styles.recentNoteAuthor}>
                          {note.name || note.created_by || note.user_name}
                          {note.impersonated_by
                            ? ` (impersonated by ${note.impersonated_by})`
                            : ""}
                        </strong>
                        <span className={styles.recentNoteTimestamp}>
                          {formatPaymentLogDateTime(note.created_at)}
                        </span>
                        {note.updated_at ? (
                          <p className={styles.recentNoteModified}>
                            Modified {formatPaymentLogDateTime(note.updated_at)}
                          </p>
                        ) : null}
                      </div>

                      <div className={styles.recentNoteBody}>
                    <div className="inline-flex max-w-full items-start gap-2 align-top">
                      {isSystem ? (
                        <span
                          className={styles.notesContent}
                          dangerouslySetInnerHTML={{
                            __html:
                              note.notes || note.note || note.note_text || "",
                          }}
                        />
                      ) : (
                        <span
                          className={styles.notesContent}
                          style={{ whiteSpace: "pre-wrap" }}
                        >
                          {note.notes || note.note || note.note_text || ""}
                        </span>
                      )}

                      {showEditButton ? (
                        <EditIconButton
                          onClick={() => setEditingNote(note)}
                          className="mt-0.5 shrink-0 p-0.5"
                          iconClassName="h-3.5 w-3.5"
                          title="Edit note"
                          aria-label="Edit note"
                          data-testid={`application-notes-tab-edit-button-${noteRowId}`}
                        />
                      ) : null}
                    </div>

                    {note.attachments && note.attachments.length > 0 ? (
                      <div style={{ marginTop: "8px" }}>
                        {note.attachments.map(
                          (attachment: NotesAttachment, attachIndex: number) => (
                            <div
                              key={attachIndex}
                              style={{ marginBottom: "4px" }}
                            >
                              {attachment.attachment_path ||
                              attachment.id ||
                              attachment.attachment_id ? (
                                <>
                                  <button
                                    type="button"
                                    className={styles.link}
                                    style={{
                                      background: "transparent",
                                      border: 0,
                                      padding: 0,
                                      textDecoration: "underline",
                                    }}
                                    onClick={() =>
                                      handlePreviewNoteAttachment(
                                        attachment,
                                        note.attachments,
                                      )
                                    }
                                  >
                                    {attachment.attachment_name || "Document"}
                                  </button>
                                  <a
                                    className={styles.link}
                                    style={{
                                      marginLeft: "16px",
                                      textDecoration: "underline",
                                      cursor:
                                        removingNoteAttachmentId !== null
                                          ? "wait"
                                          : "pointer",
                                      opacity:
                                        removingNoteAttachmentId ===
                                        (attachment.id ??
                                          attachment.attachment_id)
                                          ? 0.6
                                          : 1,
                                    }}
                                    onClick={() =>
                                      void handleRemoveNoteAttachment(attachment)
                                    }
                                  >
                                    Remove
                                  </a>
                                </>
                              ) : null}
                            </div>
                          ),
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })
              ) : (
                <ApplicationNotesListEmpty
                  variant="inline"
                  testId="application-notes-tab-filtered-empty-notes"
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
